import { useCallback, useMemo, useState } from 'react'
import type { components } from '../../api/generated/schema'
import type { Notice } from '../../lib/envelope'
import { describeApiFailure } from '../../lib/failure'
import { sceneApi, useSceneMutation } from './mutations'

type Schema = components['schemas']

/**
 * Every scene mutation, in one place.
 *
 * `canEdit` is the server's `isGameMaster`. Note what that does *not* cover: the exchange
 * counter, the turn order and the free-invoke counters are device-local and have no
 * server-side permission at all, so a player can still run their own copy of them. That is
 * a consequence of those things having no API resource, and the screen says as much.
 */
export function useSceneEditing(sceneId: number, canEdit: boolean) {
  const [notices, setNotices] = useState<Notice[]>([])
  const [failure, setFailure] = useState<string | null>(null)

  const onEnvelope = useCallback((envelope: { notices: Notice[] }) => {
    setNotices(envelope.notices)
    setFailure(null)
  }, [])

  const onFailure = useCallback((error: unknown) => {
    setNotices([])
    setFailure(describeApiFailure(error, 'Only the Game Master can change this scene.'))
  }, [])

  const base = useMemo(() => ({ sceneId, onEnvelope, onFailure }), [sceneId, onEnvelope, onFailure])

  const update = useSceneMutation<Schema['UpdateSceneRequest']>({
    ...base,
    request: (body) => sceneApi.update(sceneId, body),
  })

  const addAspect = useSceneMutation<{ description: string }>({
    ...base,
    request: ({ description }) => sceneApi.addAspect(sceneId, description),
  })

  const updateAspect = useSceneMutation<{ aspectId: number; description: string }>({
    ...base,
    request: ({ aspectId, description }) => sceneApi.updateAspect(sceneId, aspectId, description),
  })

  const removeAspect = useSceneMutation<{ aspectId: number }>({
    ...base,
    request: ({ aspectId }) => sceneApi.removeAspect(sceneId, aspectId),
  })

  const reorderAspects = useSceneMutation<{ ids: number[] }>({
    ...base,
    request: ({ ids }) => sceneApi.reorderAspects(sceneId, ids),
  })

  const addCharacter = useSceneMutation<{
    characterId: number
    role: Schema['SceneCharacterType']
  }>({
    ...base,
    request: ({ characterId, role }) => sceneApi.addCharacter(sceneId, characterId, role),
  })

  const setCharacterRole = useSceneMutation<{
    sceneCharacterId: number
    role: Schema['SceneCharacterType']
  }>({
    ...base,
    request: ({ sceneCharacterId, role }) =>
      sceneApi.setCharacterRole(sceneId, sceneCharacterId, role),
  })

  const removeCharacter = useSceneMutation<{ sceneCharacterId: number }>({
    ...base,
    request: ({ sceneCharacterId }) => sceneApi.removeCharacter(sceneId, sceneCharacterId),
  })

  const addPanel = useSceneMutation<{ description: string }>({
    ...base,
    request: ({ description }) => sceneApi.addPanel(sceneId, description),
  })

  const updatePanel = useSceneMutation<{ panelId: number; description: string }>({
    ...base,
    request: ({ panelId, description }) => sceneApi.updatePanel(sceneId, panelId, description),
  })

  const removePanel = useSceneMutation<{ panelId: number }>({
    ...base,
    request: ({ panelId }) => sceneApi.removePanel(sceneId, panelId),
  })

  const reorderPanels = useSceneMutation<{ ids: number[] }>({
    ...base,
    request: ({ ids }) => sceneApi.reorderPanels(sceneId, ids),
  })

  const regenerateSnapshot = useSceneMutation<void>({
    ...base,
    request: () => sceneApi.regenerateSnapshot(sceneId),
  })

  const setSnapshot = useSceneMutation<{ text: string }>({
    ...base,
    request: ({ text }) => sceneApi.setSnapshot(sceneId, text),
  })

  const clearSnapshot = useSceneMutation<void>({
    ...base,
    request: () => sceneApi.clearSnapshot(sceneId),
  })

  const all = [
    update,
    addAspect,
    updateAspect,
    removeAspect,
    reorderAspects,
    addCharacter,
    setCharacterRole,
    removeCharacter,
    addPanel,
    updatePanel,
    removePanel,
    reorderPanels,
    regenerateSnapshot,
    setSnapshot,
    clearSnapshot,
  ]

  return {
    canEdit,
    notices,
    failure,
    dismiss: useCallback(() => {
      setNotices([])
      setFailure(null)
    }, []),
    isPending: all.some((mutation) => mutation.isPending),
    update,
    addAspect,
    updateAspect,
    removeAspect,
    reorderAspects,
    addCharacter,
    setCharacterRole,
    removeCharacter,
    addPanel,
    updatePanel,
    removePanel,
    reorderPanels,
    regenerateSnapshot,
    setSnapshot,
    clearSnapshot,
  }
}

export type SceneEditing = ReturnType<typeof useSceneEditing>
